'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';

import { type ContactPriceFormValues, contactPriceSchema } from './contact-price-schema';
import type { TDynamicData } from '@eliadmin/core/src/types/dynamic-data';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Form, FormField, FormItem, FormMessage } from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';

import { FirstStep } from './first-step';
import { FormFooter } from './form-footer';
import { SecondStep } from './second-step';

import { motion, AnimatePresence } from 'framer-motion';

export function ContactPriceForm({
  interests,
  className,
}: {
  interests: TDynamicData[];
  className?: string;
}) {
  const [step, setStep] = useState<number>(1);

  const { t } = useTranslations();

  const form = useForm<ContactPriceFormValues>({
    resolver: zodResolver(contactPriceSchema),
    defaultValues: {
      custom: { text1416: '', text1329: '', text147: '' },
      name: '',
      email: '',
      phone: '',
      text: '',
      gdpr: false,
    },
  });

  const { mutateAsync } = useMutation({ mutationFn: sendContactMessage });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({ data: { ...data,  }, locale });
      toast({ title: t('text658'), variant: 'success' });
      router.push(ROUTES.THANK_YOU());
    } catch (err) {
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields ?? {})) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({ title: t('text657'), description: (err as any)?.message, variant: 'destructive' });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn(
          'relative z-10 flex h-[645px] w-full max-w-[528px] flex-col items-end justify-between bg-white px-4 py-12 sm:h-[589px] sm:px-6 md:px-8 lg:px-10 xl:px-12',
          className,
          'ea-contact-price-form',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-8 overflow-hidden">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -100, opacity: 0 }}
                transition={{ duration: 0.22, ease: 'easeInOut' }}
                className="w-full"
              >
                <FirstStep form={form} interests={interests} step={step} title={t('text3575')} />
              </motion.div>
            )}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -100, opacity: 0 }}
                transition={{ duration: 0.22, ease: 'easeInOut' }}
                className="w-full"
              >
                <SecondStep form={form} step={step} title={t('text360')} locale={locale} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12')}>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormFooter form={form} setStep={setStep} step={step} />
      </form>
    </Form>
  );
}
